import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

import { digitosDoCodigo, QUANTOS_DIGITOS } from './codigo-de-seis-digitos';

/**
 * O CÓDIGO EM SEIS CAMPOS (R06, p.68): um campo por dígito, como o aplicativo
 * autenticador mostra. Digitar avança; apagar no campo vazio volta; colar o
 * código inteiro (com espaço, hífen ou o que vier junto) enche os seis.
 *
 * Código errado: os seis ficam vermelhos (`erro`) e quem chama esvazia o
 * valor — o foco volta ao primeiro.
 *
 * O primeiro campo leva `autocomplete="one-time-code"`: no celular, o teclado
 * oferece o código que chegou. Para o leitor de tela, o grupo tem o nome e
 * cada campo diz qual dígito é.
 */
export function CodigoDeSeisDigitos({
  rotulo,
  valor,
  aoMudar,
  erro = false,
  desabilitado = false,
  focarAoAbrir = false,
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly erro?: boolean;
  readonly desabilitado?: boolean;
  readonly focarAoAbrir?: boolean;
}) {
  const campos = useRef<(HTMLInputElement | null)[]>([]);
  const digitos = Array.from({ length: QUANTOS_DIGITOS }, (_, i) => (valor[i] ?? '').trim());
  /* O campo vazio no meio vira espaço, para o dígito seguinte não mudar de lugar. */
  const juntar = (lista: readonly string[]) => lista.map((x) => (x === '' ? ' ' : x)).join('').trimEnd();

  const focar = (i: number) => {
    const alvo = campos.current[Math.max(0, Math.min(QUANTOS_DIGITOS - 1, i))];
    alvo?.focus();
    alvo?.select();
  };

  const escrever = (i: number, texto: string) => {
    const novos = digitosDoCodigo(texto);
    if (novos === '') return;
    /* Mais de um dígito de uma vez (colar, ou o teclado que oferece o código): enche a partir daqui. */
    const atual = digitos.slice();
    for (let k = 0; k < novos.length && i + k < QUANTOS_DIGITOS; k++) atual[i + k] = novos[k] ?? '';
    aoMudar(juntar(atual));
    focar(Math.min(QUANTOS_DIGITOS - 1, i + novos.length));
  };

  const aoTeclar = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const atual = digitos.slice();
      if (atual[i] !== '') {
        atual[i] = '';
        aoMudar(juntar(atual));
        return;
      }
      if (i > 0) {
        atual[i - 1] = '';
        aoMudar(juntar(atual));
        focar(i - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focar(i - 1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focar(i + 1);
    }
  };

  const aoColar = (i: number) => (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    escrever(i, e.clipboardData.getData('text'));
  };

  return (
    <div role="group" aria-label={rotulo} className="flex gap-2">
      {digitos.map((d, i) => (
        <input
          // A posição é a identidade do campo: o primeiro é sempre o primeiro.
          key={i}
          ref={(el) => {
            campos.current[i] = el;
          }}
          aria-label={`Dígito ${String(i + 1)} de ${String(QUANTOS_DIGITOS)}`}
          aria-invalid={erro}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          autoFocus={focarAoAbrir && i === 0}
          disabled={desabilitado}
          value={d}
          onChange={(e) => {
            escrever(i, e.target.value.replace(d, '') || e.target.value);
          }}
          onKeyDown={aoTeclar(i)}
          onPaste={aoColar(i)}
          onFocus={(e) => {
            e.target.select();
          }}
          className="controle h-14 w-12 px-0 text-center font-[family-name:var(--fonte-titulo)] text-[length:20px] font-semibold tabular-nums"
        />
      ))}
    </div>
  );
}
